import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  acceptChallenge,
  cancelChallenge,
  createChallenge,
  declineChallenge,
  getIncomingChallenges,
  getOutgoingChallenges,
  getMyChessRating,
  getMyGames,
  searchChessPlayers,
} from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import UserLink from '../../components/common/UserLink';
import { TextInput } from '../../components/common/form';
import { PieceTileGlyph } from '../../components/playing/PlayingHubMarks';
import TimeControlFields, { speedClassFromTimeControl, defaultTimeControlForSpeed } from './TimeControlFields';
import TimeModeSelect from './TimeModeSelect';
import {
  PlayingBanner,
  PlayingEmptyState,
  PlayingSkeleton,
  formatTimeControl,
  formatExpiryCountdown,
  colorPreferenceLabel,
  challengeTtlMs,
  isPastTtl,
} from './playingUi';
import { PlayingDisclosure } from './PlayingDisclosure';
import { usePlayingConfirm } from './PlayingConfirmDialog';
import {
  notifyPlayingChallengesChanged,
  PLAYING_CHALLENGES_CHANGED,
  subscribeIncomingChallenges,
} from '../../playing/chessSocket';
import { getActiveRole, getUserId } from '../../utils/auth';
import { apiErrorMessage } from '../../utils/apiError';

function playerKey(p) {
  return Number(p?.userId);
}

function samePlayer(a, b) {
  return a != null && b != null && Number(a.userId) === Number(b.userId);
}

export default function ChallengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestConfirm, dialog: confirmDialog } = usePlayingConfirm();
  const [searchParams] = useSearchParams();
  const isParentRole = String(getActiveRole() || '').toUpperCase() === 'PARENT';
  const prefillUserId = searchParams.get('userId');
  const prefillName = searchParams.get('name') || '';
  const prefillSpeed = searchParams.get('speed');
  const prefillTc = Number(searchParams.get('tc'));
  const prefillInc = Number(searchParams.get('inc'));
  const prefillDays = Number(searchParams.get('days'));
  const initialTc = (() => {
    if (Number.isFinite(prefillTc) && prefillTc > 0) {
      return {
        timeControlSeconds: prefillTc,
        incrementSeconds: Number.isFinite(prefillInc) && prefillInc >= 0 ? prefillInc : 0,
        daysPerMove: 0,
      };
    }
    if (Number.isFinite(prefillDays) && prefillDays > 0) {
      return { timeControlSeconds: 0, incrementSeconds: 0, daysPerMove: prefillDays };
    }
    if (prefillSpeed) return defaultTimeControlForSpeed(prefillSpeed);
    return { timeControlSeconds: 600, incrementSeconds: 0, daysPerMove: 0 };
  })();
  const [q, setQ] = useState(prefillName || '');
  const [players, setPlayers] = useState([]);
  const [recentOpponents, setRecentOpponents] = useState([]);
  const [searching, setSearching] = useState(false);
  const [rated, setRated] = useState(false);
  const [color, setColor] = useState('RANDOM');
  const [timeControlSeconds, setTimeControlSeconds] = useState(initialTc.timeControlSeconds);
  const [incrementSeconds, setIncrementSeconds] = useState(initialTc.incrementSeconds);
  const [daysPerMove, setDaysPerMove] = useState(initialTc.daysPerMove);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchTick, setSearchTick] = useState(0);
  const [inboxError, setInboxError] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [ttlNow, setTtlNow] = useState(() => Date.now());
  const [selected, setSelected] = useState(
    prefillUserId
      ? { userId: Number(prefillUserId), displayName: prefillName || `#${prefillUserId}`, rating: null, speedClass: null }
      : null,
  );

  const searchSpeed = daysPerMove > 0
    ? 'CORRESPONDENCE'
    : speedClassFromTimeControl(timeControlSeconds, incrementSeconds);

  const tcLabel = daysPerMove > 0
    ? t('playing.correspondenceMode', {
      defaultValue: 'Correspondence ({{n}} days per move)',
      n: daysPerMove,
    })
    : formatTimeControl(timeControlSeconds, incrementSeconds || 0);

  const loadInbox = useCallback((soft = false) => {
    if (!soft) setInboxLoading(true);
    setInboxError('');
    let incFailed = false;
    let outFailed = false;
    Promise.all([
      getIncomingChallenges().catch(() => { incFailed = true; return []; }),
      getOutgoingChallenges().catch(() => { outFailed = true; return []; }),
    ])
      .then(([inc, out]) => {
        setIncoming(Array.isArray(inc) ? inc : []);
        setOutgoing(Array.isArray(out) ? out : []);
        if (incFailed && outFailed) {
          setInboxError(t('playing.loadFailed'));
        } else if (incFailed || outFailed) {
          setInboxError(t('playing.inboxPartialLoadFailed', {
            defaultValue: 'Some challenge lists could not be loaded. Try refresh.',
          }));
        }
      })
      .finally(() => setInboxLoading(false));
  }, [t]);

  useEffect(() => {
    loadInbox();
    const iv = setInterval(() => {
      setTtlNow(Date.now());
      loadInbox(true);
    }, 30_000);
    const onChanged = () => loadInbox(true);
    window.addEventListener(PLAYING_CHALLENGES_CHANGED, onChanged);
    let unsub = () => {};
    const uid = getUserId();
    if (uid != null) {
      unsub = subscribeIncomingChallenges(uid, () => loadInbox(true));
    } else {
      getMyChessRating()
        .then((me) => {
          if (me?.userId != null) {
            unsub = subscribeIncomingChallenges(me.userId, () => loadInbox(true));
          }
        })
        .catch(() => {});
    }
    return () => {
      clearInterval(iv);
      window.removeEventListener(PLAYING_CHALLENGES_CHANGED, onChanged);
      unsub();
    };
  }, [loadInbox]);

  useEffect(() => {
    let cancelled = false;
    const me = Number(getUserId());
    getMyGames(0, 30)
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : (data?.games || []));
        const finished = new Set(['WHITE_WON', 'BLACK_WON', 'DRAW']);
        const seen = new Map();
        rows.forEach((g) => {
          const st = String(g?.status || '').toUpperCase();
          if (!finished.has(st) && !g?.endedAt) return;
          if (String(g?.gameType || '').toUpperCase() === 'VS_COMPUTER') return;
          const wid = Number(g.whiteUserId);
          const bid = Number(g.blackUserId);
          let oppId = null;
          let oppName = '';
          if (me && wid === me) {
            oppId = bid;
            oppName = g.blackName;
          } else if (me && bid === me) {
            oppId = wid;
            oppName = g.whiteName;
          }
          if (!oppId || !Number.isFinite(oppId) || seen.has(oppId)) return;
          seen.set(oppId, {
            userId: oppId,
            displayName: oppName || `#${oppId}`,
          });
        });
        setRecentOpponents([...seen.values()].slice(0, 8));
      })
      .catch(() => {
        if (!cancelled) setRecentOpponents([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setPlayers([]);
      setSearching(false);
      setSearchError('');
      return undefined;
    }
    const tmr = setTimeout(() => {
      setSearching(true);
      searchChessPlayers(query, searchSpeed)
        .then((rows) => {
          setPlayers(Array.isArray(rows) ? rows : []);
          setSearchError('');
        })
        .catch(() => {
          setPlayers([]);
          setSearchError(t('playing.searchFailed'));
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(tmr);
  }, [q, t, searchSpeed, searchTick]);

  useEffect(() => {
    if (!prefillUserId) return;
    setSelected({
      userId: Number(prefillUserId),
      displayName: prefillName || `#${prefillUserId}`,
      rating: null,
      speedClass: searchSpeed,
    });
    if (prefillName) setQ(prefillName);
  }, [prefillUserId, prefillName, searchSpeed]);

  const selectOpponent = useCallback((p) => {
    if (!p?.userId) return;
    setSelected({
      userId: Number(p.userId),
      displayName: p.displayName || `#${p.userId}`,
      rating: p.rating ?? null,
      speedClass: p.speedClass || searchSpeed,
    });
    setMessage('');
    setSentTo(null);
    setError('');
  }, [searchSpeed]);

  const sendChallenge = useCallback(async () => {
    if (!selected?.userId || sending) return;
    if (rated && !(await requestConfirm({ message: t('playing.ratedConfirm') }))) return;
    setSending(true);
    setMessage('');
    setError('');
    try {
      const corr = daysPerMove > 0;
      const ch = await createChallenge({
        toUserId: selected.userId,
        colorPreference: color,
        rated,
        timeControlSeconds: corr ? 0 : timeControlSeconds,
        incrementSeconds: corr ? 0 : incrementSeconds,
        daysPerMove: corr ? daysPerMove : null,
      });
      if (ch?.gameId) {
        navigate(`/playing/game/${ch.gameId}`);
        return;
      }
      setSentTo({ id: selected.userId, name: selected.displayName });
      setMessage(t('playing.challengeSent'));
      loadInbox();
    } catch (e) {
      setError(apiErrorMessage(e, t('playing.challengeFailed')));
    } finally {
      setSending(false);
    }
  }, [
    selected, sending, rated, requestConfirm, t, daysPerMove, color,
    timeControlSeconds, incrementSeconds, navigate, loadInbox,
  ]);

  if (isParentRole) {
    return (
      <div className="page-shell-form playing-page max-w-2xl">
        <PageHeaderSection
          backTo="/playing"
          eyebrow={t('nav.playingPlatform')}
          title={t('playing.challengePlayer')}
          subtitle={t('playing.parentChallengeBlockedHint', {
            defaultValue: 'Parents can play vs the computer and watch their kids — human challenges are for players.',
          })}
        />
        <PlayingEmptyState
          title={t('playing.parentChallengeBlockedTitle', {
            defaultValue: 'Challenges are for players',
          })}
          hint={t('playing.parentChallengeBlockedHint', {
            defaultValue: 'Parents can play vs the computer and watch their kids — human challenges are for players.',
          })}
          action={(
            <div className="flex flex-wrap gap-2">
              <Button variant="page-primary" onClick={() => navigate('/playing/play')}>
                {t('playing.playComputer', { defaultValue: 'Play computer' })}
              </Button>
              <Button variant="page-secondary" onClick={() => navigate('/playing')}>
                {t('playing.backToHub', { defaultValue: 'Back to hub' })}
              </Button>
            </div>
          )}
        />
      </div>
    );
  }

  const liveIncoming = incoming.filter((c) => !isPastTtl(c.createdAt, challengeTtlMs(c), ttlNow));
  const liveOutgoing = outgoing.filter((c) => !isPastTtl(c.createdAt, challengeTtlMs(c), ttlNow));
  const showSearchResults = q.trim().length >= 2;

  return (
    <div className="page-shell-form playing-page playing-page--challenge max-w-3xl">
      <PageHeaderSection
        backTo="/playing"
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.challengePlayer')}
        subtitle={t('playing.challengeSubtitle')}
      />
      {error && <PlayingBanner tone="danger">{error}</PlayingBanner>}
      {inboxError && (
        <PlayingBanner tone="warning">
          <p>{inboxError}</p>
          <div className="mt-2">
            <Button variant="page-secondary" onClick={() => loadInbox()}>
              {t('playing.retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        </PlayingBanner>
      )}
      {searchError && <PlayingBanner tone="danger">{searchError}</PlayingBanner>}
      {message && (
        <PlayingBanner tone="success">
          <p>{message}</p>
          {sentTo && (
            <p className="mt-1 text-sm opacity-90">
              <Trans
                i18nKey="playing.challengeSentHint"
                values={{ name: sentTo.name }}
                components={{
                  profile: (
                    <UserLink
                      id={sentTo.id}
                      name={sentTo.name}
                      className="text-inherit hover:underline"
                    />
                  ),
                }}
              />
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="page-primary" onClick={() => navigate('/playing')}>
              {t('playing.backHub')}
            </Button>
            <Button
              variant="page-secondary"
              onClick={() => {
                setMessage('');
                setSentTo(null);
                setSelected(null);
                setQ('');
              }}
            >
              {t('playing.challengeAnother')}
            </Button>
          </div>
        </PlayingBanner>
      )}

      {!inboxLoading && liveIncoming.length > 0 && (
        <PlayingDisclosure
          className="mb-4"
          title={t('playing.incomingChallenges', { defaultValue: 'Incoming challenges' })}
          badge={liveIncoming.length}
          defaultOpen
        >
          <ul className="divide-y divide-stone-100">
            {liveIncoming.map((c) => {
              const tc = formatTimeControl(c.timeControlSeconds, c.incrementSeconds || 0, c.daysPerMove);
              const expires = formatExpiryCountdown(c.createdAt, challengeTtlMs(c), ttlNow);
              return (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-1 py-3">
                  <div>
                    <p className="text-sm font-semibold text-stone-900">
                      <UserLink id={c.fromUserId} name={c.fromName} playing className="text-stone-900 hover:text-primary-700" />
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {tc && <span className="playing-tc-chip playing-tc-chip--sm">{tc}</span>}
                      <span className="playing-meta-pill">{c.rated ? t('playing.ratedGame') : t('playing.casualGame')}</span>
                      <span className="playing-meta-pill">{colorPreferenceLabel(c.colorPreference, t)}</span>
                      {expires && (
                        <span className="playing-meta-pill playing-meta-pill--warn">
                          {t('playing.expiresIn', { when: expires, defaultValue: 'Expires in {{when}}' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="page-primary"
                      disabled={busyId === c.id}
                      onClick={async () => {
                        setBusyId(c.id);
                        try {
                          const g = await acceptChallenge(c.id);
                          notifyPlayingChallengesChanged();
                          navigate(`/playing/game/${g.id}`);
                        } catch {
                          setError(t('playing.challengeAcceptFailed'));
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      {t('playing.accept')}
                    </Button>
                    <Button
                      variant="page-secondary"
                      disabled={busyId === c.id}
                      onClick={async () => {
                        setBusyId(c.id);
                        try {
                          await declineChallenge(c.id);
                          setIncoming((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
                          notifyPlayingChallengesChanged();
                        } catch {
                          setError(t('playing.challengeDeclineFailed', { defaultValue: 'Could not decline. Try again.' }));
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      {t('playing.decline')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </PlayingDisclosure>
      )}

      {!inboxLoading && liveOutgoing.length > 0 && (
        <PlayingDisclosure
          className="mb-4"
          title={t('playing.outgoingChallenges')}
          badge={liveOutgoing.length}
          defaultOpen={liveIncoming.length === 0}
        >
          <ul className="divide-y divide-stone-100">
            {liveOutgoing.map((c) => {
              const tc = formatTimeControl(c.timeControlSeconds, c.incrementSeconds || 0, c.daysPerMove);
              const expires = formatExpiryCountdown(c.createdAt, challengeTtlMs(c), ttlNow);
              return (
                <li key={`out-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-1 py-3">
                  <div>
                    <p className="text-sm font-semibold text-stone-900">
                      <UserLink id={c.toUserId} name={c.toName || t('playing.challengePlayer')} playing className="text-stone-900 hover:text-primary-700" />
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {tc && <span className="playing-tc-chip playing-tc-chip--sm">{tc}</span>}
                      <span className="playing-meta-pill">{c.rated ? t('playing.ratedGame') : t('playing.casualGame')}</span>
                      {expires && (
                        <span className="playing-meta-pill playing-meta-pill--warn">
                          {t('playing.expiresIn', { when: expires, defaultValue: 'Expires in {{when}}' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.gameId && (
                      <Button variant="page-primary" onClick={() => navigate(`/playing/game/${c.gameId}`)}>
                        {t('playing.open')}
                      </Button>
                    )}
                    <Button
                      variant="page-secondary"
                      disabled={busyId === `cancel-${c.id}`}
                      onClick={async () => {
                        setBusyId(`cancel-${c.id}`);
                        try {
                          await cancelChallenge(c.id);
                          setOutgoing((prev) => prev.filter((x) => Number(x.id) !== Number(c.id)));
                          notifyPlayingChallengesChanged();
                        } catch {
                          setError(t('playing.cancelChallengeFailed'));
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      {t('playing.cancelChallenge')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </PlayingDisclosure>
      )}

      {/* 1 — Opponent */}
      <section className="playing-challenge-panel" aria-labelledby="challenge-opponent-title">
        <div className="playing-challenge-panel__head">
          <span className="playing-challenge-panel__step" aria-hidden>1</span>
          <div>
            <h2 id="challenge-opponent-title" className="playing-challenge-panel__title">
              {t('playing.chooseOpponent', { defaultValue: 'Choose opponent' })}
            </h2>
            <p className="playing-challenge-panel__hint">
              {t('playing.chooseOpponentHint', {
                defaultValue: 'Type a name to search, or pick someone you played recently.',
              })}
            </p>
          </div>
        </div>
        <div className="playing-challenge-panel__body">
          {selected && (
            <div className="playing-challenge-selected">
              <div>
                <p className="playing-challenge-selected__meta">
                  {t('playing.selectedOpponent', { defaultValue: 'Selected' })}
                </p>
                <p className="playing-challenge-selected__name">
                  <UserLink
                    id={selected.userId}
                    name={selected.displayName}
                    playing
                    className="text-stone-900 hover:text-primary-700"
                  />
                </p>
                {selected.rating != null && (
                  <p className="playing-challenge-selected__meta">
                    {t(`playing.speed.${selected.speedClass || searchSpeed}`, {
                      defaultValue: selected.speedClass || searchSpeed,
                    })}
                    {': '}
                    {selected.rating}
                  </p>
                )}
              </div>
              <Button
                variant="page-secondary"
                onClick={() => {
                  setSelected(null);
                  setQ('');
                }}
              >
                {t('playing.changeOpponent', { defaultValue: 'Change' })}
              </Button>
            </div>
          )}

          <TextInput
            label={t('playing.searchPlayers')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('playing.searchPlayersPlaceholder')}
            autoComplete="off"
          />

          {recentOpponents.length > 0 && !showSearchResults && (
            <div>
              <p className="playing-hub-field-label">
                {t('playing.recentOpponents', { defaultValue: 'Recent opponents' })}
              </p>
              <div className="playing-challenge-chip-row mt-2">
                {recentOpponents.map((s) => (
                  <button
                    key={`opp-${s.userId}`}
                    type="button"
                    className={`playing-challenge-chip${samePlayer(selected, s) ? ' playing-challenge-chip--active' : ''}`}
                    onClick={() => selectOpponent(s)}
                  >
                    {s.displayName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showSearchResults ? (
            <div>
              <p className="playing-hub-field-label">
                {t('playing.searchResults', { defaultValue: 'Search results' })}
              </p>
              <div className="mt-2 overflow-hidden rounded-md border border-stone-200">
                {searching ? (
                  <div className="p-3">
                    <PlayingSkeleton rows={3} />
                  </div>
                ) : searchError ? (
                  <PlayingEmptyState
                    title={t('playing.searchFailed')}
                    hint={t('playing.searchFailedHint')}
                    action={(
                      <Button variant="page-secondary" onClick={() => setSearchTick((n) => n + 1)}>
                        {t('playing.retry', { defaultValue: 'Retry' })}
                      </Button>
                    )}
                  />
                ) : players.length === 0 ? (
                  <PlayingEmptyState
                    title={t('playing.noPlayersFound')}
                    hint={t('playing.noPlayersFoundHint')}
                  />
                ) : (
                  <ul className="divide-y divide-stone-100">
                    {players.map((p) => (
                      <li key={playerKey(p)}>
                        <button
                          type="button"
                          className={`playing-challenge-pick-row${samePlayer(selected, p) ? ' playing-challenge-pick-row--active' : ''}`}
                          onClick={() => selectOpponent(p)}
                        >
                          <span>
                            <span className="playing-challenge-pick-row__name block">{p.displayName}</span>
                            <span className="playing-challenge-pick-row__meta block">
                              {t(`playing.speed.${p.speedClass || searchSpeed}`, {
                                defaultValue: p.speedClass || searchSpeed,
                              })}
                              {': '}
                              {p.rating}
                            </span>
                          </span>
                          <span className="text-xs font-medium text-stone-400">
                            {samePlayer(selected, p)
                              ? t('playing.selectedOpponent', { defaultValue: 'Selected' })
                              : t('playing.select', { defaultValue: 'Select' })}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : !selected && recentOpponents.length === 0 ? (
            <PlayingEmptyState
              title={t('playing.typeToSearch')}
              hint={t('playing.typeToSearchHint')}
            />
          ) : null}
        </div>
      </section>

      {/* 2 — Settings */}
      <section
        id="playing-challenge-create"
        className="playing-challenge-panel"
        aria-labelledby="challenge-settings-title"
      >
        <div className="playing-challenge-panel__head">
          <span className="playing-challenge-panel__step" aria-hidden>2</span>
          <div>
            <h2 id="challenge-settings-title" className="playing-challenge-panel__title">
              {t('playing.gameSettings', { defaultValue: 'Game settings' })}
            </h2>
            <p className="playing-challenge-panel__hint">
              {t('playing.gameSettingsHint', {
                defaultValue: 'Time control, your color, and rated options.',
              })}
            </p>
          </div>
        </div>
        <div className="playing-challenge-panel__body">
          <TimeModeSelect layout="segmented" value={daysPerMove} onChange={setDaysPerMove} />
          {daysPerMove <= 0 ? (
            <TimeControlFields
              layout="hub"
              timeControlSeconds={timeControlSeconds}
              incrementSeconds={incrementSeconds}
              onChange={({ timeControlSeconds: tc, incrementSeconds: inc }) => {
                setTimeControlSeconds(tc);
                setIncrementSeconds(inc);
              }}
            />
          ) : (
            <p className="text-sm text-stone-600">
              {t('playing.correspondenceHint', {
                n: daysPerMove,
                defaultValue: 'Each player has {{n}} days to make a move. No ticking clock.',
              })}
            </p>
          )}

          <div>
            <p className="playing-hub-field-label">{t('playing.yourColor')}</p>
            <div className="playing-hub-color-row mt-2" role="radiogroup" aria-label={t('playing.yourColor')}>
              {[
                { value: 'WHITE', glyph: 'white', label: t('playing.colorWhite') },
                { value: 'RANDOM', glyph: 'random', label: t('playing.colorRandom') },
                { value: 'BLACK', glyph: 'black', label: t('playing.colorBlack') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={color === opt.value}
                  className={`playing-hub-color-tile playing-hub-color-tile--${opt.glyph}${
                    color === opt.value ? ' playing-hub-color-tile--selected' : ''
                  }`}
                  onClick={() => setColor(opt.value)}
                >
                  <PieceTileGlyph color={opt.glyph} />
                  <span className="playing-hub-color-tile__label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="playing-challenge-options">
            <label className="playing-hub-check">
              <input
                type="checkbox"
                className="rounded border-stone-300 text-primary-600 focus:ring-primary-200"
                checked={rated}
                onChange={(e) => setRated(e.target.checked)}
              />
              {t('playing.ratedGame')}
            </label>
          </div>
        </div>
      </section>

      <div className="playing-challenge-sendbar">
        <div className="playing-challenge-sendbar__inner">
          <div className="playing-challenge-sendbar__summary">
            {selected ? (
              <>
                <p>
                  {t('playing.challengeTo', {
                    defaultValue: 'Challenge',
                  })}
                  {' '}
                  <strong>{selected.displayName}</strong>
                </p>
                <div className="playing-challenge-sendbar__meta">
                  {tcLabel && <span className="playing-tc-chip playing-tc-chip--sm">{tcLabel}</span>}
                  <span className="playing-meta-pill">{rated ? t('playing.ratedGame') : t('playing.casualGame')}</span>
                  <span className="playing-meta-pill">{colorPreferenceLabel(color, t)}</span>
                </div>
              </>
            ) : (
              <p>
                {t('playing.pickOpponentFirst', {
                  defaultValue: 'Pick an opponent above to send a challenge.',
                })}
              </p>
            )}
          </div>
          <Button
            variant="page-primary"
            className="!w-auto min-w-[10rem] shrink-0"
            disabled={!selected || sending}
            onClick={sendChallenge}
          >
            {sending
              ? t('playing.sending', { defaultValue: 'Sending…' })
              : t('playing.sendChallenge', { defaultValue: 'Send a challenge' })}
          </Button>
        </div>
      </div>

      {confirmDialog}
    </div>
  );
}
